/**
 * Hook for fetching and managing dashboard data.
 * Provides company dashboard stats including listings, agents, and usage.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { fetchMyDashboard } from '../services/companyService';
import type { CompanyDashboard } from '../types/company';

interface UseDashboardReturn {
  data: CompanyDashboard | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboard(): UseDashboardReturn {
  const { session } = useAuth();
  const [data, setData] = useState<CompanyDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!session?.token) {
      setIsLoading(false);
      setError('Not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const dashboardData = await fetchMyDashboard(session.token);
      setData(dashboardData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(message);
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session?.token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}
